import React, { useState } from 'react'
import { format } from 'date-fns'
import { createFileRoute } from '@tanstack/react-router'
import { useShallow } from 'zustand/react/shallow'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Layout } from '@/components/custom/layout'
import CampaignFilter from '@/components/campaign-route/campaign-filter'
import CampaignTable from '@/components/campaign-route/campaign-table'
import Pagination from '@/components/custom/pagination'
import { CreateCampaignModal } from '@/components/campaign-route/create-campaign'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/custom/button'
import PopupConfirm from '@/components/custom/popup-confirm'
import {
  // ChartColumnIncreasing,
  Plus,
  Search,
  RefreshCcw,
  Download,
  Trash2,
} from 'lucide-react'
import {
  useCampaignListFilterStore,
  GET_CAMPAIGNS_KEY,
} from '@/store/campaign-list-filter-store'
import {
  searchCampaigns,
  exportCampaign,
  deleteCampaigns,
} from '@/api/campaign'
// import { useNavigate } from '@tanstack/react-router'
import { navigateGuard } from '@/utils/auth.js'
import { UserRole } from '@/constants/user.js'
import { toast } from 'sonner'
import { saveExcelFile } from '@/utils/export.js'
import { throwException } from '@/exceptions/throw-exception.js'

export const Route = createFileRoute('/_auth/campaign/')({
  beforeLoad: ({ location }) =>
    navigateGuard(location, [UserRole.ADMINISTRATOR, UserRole.SUPERVISOR]),
  component: Campaign,
})

function Campaign() {
  // const navigate = useNavigate()
  const [isOpenModal, setIsOpenModal] = useState(false)
  const [rowSelection, setRowSelection] = useState({})
  const queryClient = useQueryClient()
  const [
    page,
    setPage,
    pageSize,
    setPageSize,
    filter,
    loadTime,
    setTriggerFilter,
    resetFilter,
  ] = useCampaignListFilterStore(
    useShallow((state) => [
      state.page,
      state.setPage,
      state.pageSize,
      state.setPageSize,
      state.filter,
      state.loadTime,
      state.setTriggerFilter,
      state.resetFilter,
    ]),
  )

  const { isLoading, data: queryData } = useQuery({
    queryKey: [GET_CAMPAIGNS_KEY, page, pageSize, loadTime],
    queryFn: async () => {
      const fromDate = format(filter.rangeDate.from, 'yyyy-MM-dd')
      const toDate = format(filter.rangeDate.to, 'yyyy-MM-dd')
      const params = {
        from: fromDate,
        to: toDate,
        type: 'all',
        offset: (page - 1) * pageSize,
        limit: pageSize,
      }

      if (filter.type !== 'all') {
        params.type = filter.type
      }

      if (filter.name !== '') {
        params.name = filter.name
      }
      try {
        const { data } = await searchCampaigns({ params })
        return data
      } catch (error) {
        throwException(error)
        return []
      }
    },
  })

  const handleExportCampaign = () => {
    const params = {
      from: format(filter.rangeDate.from, 'yyyy-MM-dd'),
      to: format(filter.rangeDate.to, 'yyyy-MM-dd'),
      offset: (page - 1) * pageSize,
      limit: pageSize,
    }

    if (filter.name) {
      params.name = filter.name
    }

    if (filter.type !== 'all') {
      params.type = filter.type
    }

    return exportCampaign({ params })
  }

  const { isPending: isExporting, mutate: handleExport } = useMutation({
    mutationFn: handleExportCampaign,
    onSuccess: (data) => {
      saveExcelFile(data, 'DanhSachChienDich')
    },
    onError: throwException,
  })

  const { mutate: handleDeleteCampaigns } = useMutation({
    mutationFn: () => {
      const campaignIds = selectedRowsIndex.map(
        (selectedIndex) => campaigns[selectedIndex]._id,
      )

      deleteCampaigns({ campaignIds })
    },
    onSuccess: () => {
      toast.success('Xóa chiến dịch thành công')

      setRowSelection({})

      queryClient.invalidateQueries({
        queryKey: [GET_CAMPAIGNS_KEY],
      })
    },
    onError: throwException,
  })

  const campaigns = isLoading ? [] : (queryData?.list ?? [])
  const totalRecords = isLoading ? 0 : (queryData?.total ?? 0)

  const selectedRowsIndex = Object.keys(rowSelection).filter(
    (rowIndex) => rowSelection[rowIndex],
  )

  return (
    <React.Fragment>
      <Layout.Body className="flex w-full flex-col gap-3 overflow-hidden">
        <Card className="sticky top-0">
          <CardContent className="flex items-center justify-between p-4">
            <h1 className="text-lg font-bold">Danh sách chiến dịch</h1>
            <div className="flex items-center gap-[10px]">
              {/* <Button
                className="bg-[#2f8a00]"
                leftSection={
                  <ChartColumnIncreasing className="h-4 w-4 -translate-y-[1px]" />
                }
                onClick={() => navigate({ to: '/campaign/report' })}
              >
                Xem báo cáo
              </Button> */}
              <Button
                leftSection={<Plus className="h-4 w-4 -translate-y-[1px]" />}
                onClick={() => setIsOpenModal(true)}
              >
                Thêm chiến dịch
              </Button>
            </div>
          </CardContent>
        </Card>
        <Card className="h-full w-full overflow-auto">
          <CardContent className="flex h-full flex-col space-y-4 p-4">
            <div className="sticky top-0 flex items-end justify-between">
              <CampaignFilter />
              <div className="flex gap-2">
                <Button
                  leftSection={<Search className="h-4 w-4" />}
                  onClick={() => setTriggerFilter()}
                >
                  <span>Lọc</span>
                </Button>
                <Button variant="outline" onClick={() => resetFilter()}>
                  <RefreshCcw className="h-4 w-4 text-[#333333]" />
                </Button>
                <Button
                  variant="outline-secondary"
                  loading={isExporting}
                  onClick={() => handleExport()}
                >
                  <Download className="h-4 w-4" />
                </Button>
                {selectedRowsIndex.length > 0 && (
                  <PopupConfirm
                    trigger={
                      <Button variant="outline">
                        <Trash2 className="h-4 w-4 cursor-pointer text-[hsl(var(--icon-danger))]" />
                      </Button>
                    }
                    title={'Xóa chiến dịch'}
                    description={`Bạn có chắc chắn muốn xóa các chiến dịch được lựa chọn không?`}
                    onConfirm={() => handleDeleteCampaigns()}
                  />
                )}
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <CampaignTable
                isLoading={isLoading}
                campaigns={campaigns}
                rowSelection={rowSelection}
                setRowSelection={setRowSelection}
              />
            </div>
            <div className="sticky bottom-0">
              <Pagination
                totalRecords={totalRecords}
                page={page}
                pageSize={pageSize}
                setPage={setPage}
                setPageSize={setPageSize}
              />
            </div>
          </CardContent>
        </Card>
      </Layout.Body>
      <CreateCampaignModal
        isOpen={isOpenModal}
        handleCloseModal={() => setIsOpenModal(false)}
      />
    </React.Fragment>
  )
}
